import { useEffect, useState } from 'react';
import { Download, ChevronDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { ExportableTable, exportMerged, exportTable, findExportableTables, mergeTables } from '../utils/tableExport';

// Pulsante "Export" nell'intestazione: elenca le tabelle presenti nella pagina corrente e ne
// scarica una in CSV (vedi utils/tableExport.ts — esporta ciò che è visibile). Universale: vale
// per ogni pagina senza che la pagina sappia nulla. Compare solo dove c'è davvero qualcosa da
// scaricare (26/10/2026): su Overview e simili, senza tabelle, il pulsante non si mostra.
export default function ExportMenu() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tables, setTables] = useState<ExportableTable[]>([]);

  // Le tabelle si contano all'apertura del menu e, finché non se ne trova una, ogni pochi
  // secondi: la pagina può ancora stare caricando. Trovata una tabella il pulsante resta (il
  // conteggio esatto si rifà all'apertura), così non si convertono di continuo griglie grandi.
  const [count, setCount] = useState(0);
  useEffect(() => {
    setOpen(false);
    setCount(0);
    const recount = () => setCount(c => (c > 0 ? c : findExportableTables().length));
    const first = setTimeout(recount, 800);
    const interval = setInterval(recount, 3000);
    return () => { clearTimeout(first); clearInterval(interval); };
  }, [location.pathname, location.hash]);

  function toggle() {
    if (!open) {
      const found = findExportableTables();
      setTables(found);
      setCount(found.length);
    }
    setOpen(v => !v);
  }

  function pageTitle(): string {
    return document.querySelector('main h1')?.textContent?.replace(/\s+/g, ' ').trim() || 'soc-platform';
  }

  if (count === 0 && !open) return null;

  return (
    <div className="relative">
      <button onClick={toggle} title="Export the tables on this page as CSV (what is currently shown: visible columns, loaded rows, current sorting and filters)"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-gray-800 transition-colors">
        <Download size={15} /> Export{count > 0 && <span className="text-xs text-gray-500">({count})</span>} <ChevronDown size={14} className="text-gray-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-80 card z-20 text-left">
            <div className="px-4 py-2 border-b border-gray-100 text-xs text-gray-500">
              {tables.length === 0 ? 'No table with data on this page.' : 'Download as CSV (opens in Excel). Exports what is shown: visible columns, loaded rows, current sorting and filters.'}
            </div>
            {tables.length > 1 && mergeTables(tables) && (
              <button onClick={() => { exportMerged(tables, pageTitle()); setOpen(false); }}
                className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 border-b border-gray-100">
                <span>All {tables.length} tables in one file</span>
                <span className="text-xs text-gray-400 shrink-0 ml-2">{tables.reduce((n, t) => n + t.rows, 0)} rows</span>
              </button>
            )}
            {tables.map((t, i) => (
              <button key={i} onClick={() => { exportTable(t, pageTitle()); setOpen(false); }}
                className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <span className="truncate">{t.name}</span>
                <span className="text-xs text-gray-400 shrink-0 ml-2">{t.rows} row{t.rows === 1 ? '' : 's'}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
